/**
 * Clasificador Fisiológico de Arquitectura y Fases del Sueño (Criterios AASM & Actigrafía Torácica).
 * 
 * ¿Cómo sabe la plataforma que el usuario está durmiendo?
 * 1. HORA LOCAL (Ventana Circadiana):
 *    - Sueño nocturno: horas locales entre 20:30 y 11:30.
 *    - Siesta reparadora: horas locales entre 12:30 y 17:30 (exclusivamente si la postura es 'acostado' y pasos = 0).
 *    - Períodos de vigilia activa (tarde/día de pie o sentado) se descartan automáticamente.
 * 2. BIOMECÁNICA (Postura en decúbito e Inmovilidad):
 *    - El usuario debe estar en decúbito horizontal (postura 'acostado').
 *    - Ausencia sostenida de pasos (steps = 0) y aceleración torácica estable (jerk < 3.5).
 * 3. CARDIOVASCULAR (Caída simpática hacia el Nadir):
 *    - Al quedarse dormido (Sleep Onset), la FC basal desciende entre 8% y 25% respecto a la vigilia diurna.
 * 
 * Mapeo de niveles verticales (estilo Amazon Halo / AASM):
 *  - Nivel 4 (Superior):  Despierto  (#ff9f0a - Ámbar)
 *  - Nivel 3 (Medio-Alto): REM       (#64d2ff - Celeste Aqua)
 *  - Nivel 2 (Medio-Bajo): Ligero    (#007aff - Azul Eléctrico)
 *  - Nivel 1 (Fondo):      Profundo  (#5856d6 - Índigo Violeta)
 */

export const SLEEP_STAGES_CONFIG = {
  awake: {
    level: 4,
    key: 'awake',
    name: 'Despierto',
    color: '#ff9f0a',
    targetMinPct: 3,
    targetMaxPct: 10,
    desc: 'Micro-despertares, giros en la cama o transiciones conscientes.'
  },
  rem: {
    level: 3,
    key: 'rem',
    name: 'REM',
    color: '#64d2ff',
    targetMinPct: 20,
    targetMaxPct: 25,
    desc: 'Fase de sueños vívidos, atonía muscular motora y consolidación neurocognitiva.'
  },
  light: {
    level: 2,
    key: 'light',
    name: 'Ligero',
    color: '#007aff',
    targetMinPct: 45,
    targetMaxPct: 55,
    desc: 'Base del descanso (fases N1 y N2), relajación muscular y desaceleración progresiva.'
  },
  deep: {
    level: 1,
    key: 'deep',
    name: 'Profundo',
    color: '#5856d6',
    targetMinPct: 15,
    targetMaxPct: 25,
    desc: 'Fase N3 (Ondas Lentas / Delta): reparación tisular, secreción de GH y limpieza celular glinfática.'
  }
}

function fmtClock(ms) {
  const d = new Date(ms)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

function formatDuration(mins) {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/**
 * Filtra y detecta si el bloque de lecturas corresponde a un período real de sueño según hora local, postura e inmovilidad.
 */
export function analyzeSleepArchitecture(readings) {
  if (!readings || readings.length < 30) return null

  const sorted = [...readings].sort((a, b) => a.time - b.time)

  // 1. FILTRADO FISIOLÓGICO Y CIRCADIANO ESTRICTO:
  // Un descanso nocturno que termina en este día DEBE haber iniciado durante la noche o madrugada.
  // Si las lecturas recién comienzan a las 06:30, 07:00, 08:13 AM, etc., el usuario arrancó a
  // usar la banda DE DÍA / YA DESPIERTO.
  
  // Buscar lecturas que hayan ocurrido en la noche o madrugada (entre 21:00 y 06:00 AM local)
  const nocturnalReadings = sorted.filter(r => {
    const h = new Date(r.time).getHours()
    return h >= 21 || h < 6
  })

  // Para considerar siesta en la tarde: debe ocurrir entre 12:30 y 17:30 con bradicardia marcada (< 65 bpm)
  let isNap = false
  let napCandidates = []
  if (nocturnalReadings.length < 25) {
    napCandidates = sorted.filter(r => {
      const d = new Date(r.time)
      const h = d.getHours()
      const m = d.getMinutes()
      const t = h + m / 60
      return t >= 12.5 && t <= 17.5 && r.hr && r.hr <= 64 && (r.posture ? r.posture === 'acostado' : true)
    })
    isNap = napCandidates.length >= 25
  }

  // Si no hay datos en la madrugada/noche y tampoco hay una siesta comprobada con bradicardia:
  // 👉 EL USUARIO ESTABA DESPIERTO. No se inventa sueño.
  if (nocturnalReadings.length < 25 && !isNap) {
    return null
  }

  // Si es un sueño nocturno genuino:
  // Acotamos el final del sueño (Wake Up):
  // Si las lecturas continúan en la mañana, detectamos el momento del despertar.
  // El despertar matutino ocurre cuando después de las 06:00 AM el pulso sube sostenidamente
  // por encima de 75 bpm o cuando termina la bradicardia nocturna.
  let sleepCandidates = []
  if (isNap) {
    sleepCandidates = napCandidates
  } else {
    // Tomar desde el inicio nocturno hasta que la persona se despierte en la mañana
    const candidates = sorted.filter(r => {
      const d = new Date(r.time)
      const h = d.getHours()
      const m = d.getMinutes()
      const t = h + m / 60
      // Ventana nocturna genuina: noche anterior (>= 21.0) hasta media mañana (<= 10.5 si el pulso sigue en reposo)
      return (t >= 21.0 || t <= 10.5)
    })

    // Cortar el final del sueño en el momento del despertar:
    // Encontrar el último tramo donde el pulso se mantuvo en reposo (< 75 bpm) en la mañana
    let wakeIdx = candidates.length - 1
    for (let i = 0; i < candidates.length; i++) {
      const d = new Date(candidates[i].time)
      const h = d.getHours()
      if (h >= 6) {
        // Si después de las 6 AM el pulso supera 76 bpm de forma sostenida (5 lecturas seguidas) o hay pasos:
        const nextBatch = candidates.slice(i, i + 8)
        const isAwakeActivity = nextBatch.length >= 4 && nextBatch.every(r => (r.hr && r.hr >= 75) || (r.steps && r.steps > 0) || (r.posture && r.posture !== 'acostado'))
        if (isAwakeActivity) {
          wakeIdx = Math.max(0, i - 1)
          break
        }
      }
    }
    sleepCandidates = candidates.slice(0, wakeIdx + 1)
  }

  // Si tras acotar el período no quedan al menos 25 minutos de sueño, descartar
  if (sleepCandidates.length < 25) return null

  const startMs = sleepCandidates[0].time
  const endMs   = sleepCandidates[sleepCandidates.length - 1].time
  const totalSpanMin = Math.round((endMs - startMs) / 60000)

  if (totalSpanMin < 25) return null

  // 2. Extraer pulso basal nocturno y Nadir (punto más bajo de la noche)
  const validHrs = sleepCandidates.map(r => r.hr).filter(h => h && h >= 35 && h <= 180)
  if (validHrs.length < 15) return null

  validHrs.sort((a, b) => a - b)
  const nadirHr = validHrs[Math.floor(validHrs.length * 0.05)] || 48 // Percentil 5 para evitar ruido
  const avgSleepHr = Math.round(validHrs.reduce((a, b) => a + b, 0) / validHrs.length)

  // 3. Segmentación en épocas estándar de 60 segundos
  const epochSizeMs = 60 * 1000
  const epochs = []
  let curT = startMs
  let epochIdx = 0

  while (curT < endMs) {
    const nextT = curT + epochSizeMs
    const chunk = sleepCandidates.filter(r => r.time >= curT && r.time < nextT)

    if (chunk.length > 0) {
      const avgHr = Math.round(chunk.reduce((a, b) => a + (b.hr || avgSleepHr), 0) / chunk.length)
      const hasMovement = chunk.some(r => (r.steps && r.steps > 0) || (r.posture && r.posture !== 'acostado'))

      // Calcular RMSSD local del minuto (milisegundos)
      const rrList = chunk.flatMap(r => r.rr || []).filter(v => v > 320 && v < 1900)
      let rmssd = null
      if (rrList.length >= 3) {
        const diffs = rrList.slice(1).map((v, i) => v - rrList[i])
        rmssd = Math.round(Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length))
      }

      // Proporción temporal en la noche (0.0 al inicio, 1.0 al despertar)
      const nightProgress = (curT - startMs) / (endMs - startMs)

      // ── Clasificación Biomédica según Criterios AASM ──
      let stageKey = 'light'

      if (hasMovement || avgHr > avgSleepHr + 14) {
        // Micro-despertar o taquicardia transitoria con agitación
        stageKey = 'awake'
      } else if (avgHr <= nadirHr + 4 && (rmssd === null || rmssd >= 42) && nightProgress < 0.65) {
        // Ondas lentas / Delta (N3): Nadir de frecuencia, máxima sincronía vagal, predominio en primera mitad
        stageKey = 'deep'
      } else if (avgHr > avgSleepHr + 1 && nightProgress > 0.30 && !hasMovement) {
        // Fase REM: Desincronización cortical, ráfagas fásicas de pulso, predominio en segunda mitad
        stageKey = 'rem'
      } else {
        // Fase Ligera (N1 y N2): Transición basal estable
        stageKey = 'light'
      }

      const cfg = SLEEP_STAGES_CONFIG[stageKey]
      epochs.push({
        idx: epochIdx,
        time: curT,
        timeLabel: fmtClock(curT),
        stage: stageKey,
        level: cfg.level,
        color: cfg.color,
        label: cfg.name,
        desc: cfg.desc,
        hr: avgHr,
        rmssd: rmssd || 38,
      })
      epochIdx++
    }
    curT = nextT
  }

  if (!epochs.length) return null

  // 4. Suavizado fisiológico (las fases no duran 60 segundos aislados; agrupar micro-bloques)
  for (let i = 1; i < epochs.length - 1; i++) {
    if (epochs[i - 1].stage === epochs[i + 1].stage && epochs[i].stage !== epochs[i - 1].stage) {
      if (epochs[i].stage !== 'awake') {
        epochs[i].stage = epochs[i - 1].stage
        epochs[i].level = epochs[i - 1].level
        epochs[i].color = epochs[i - 1].color
        epochs[i].label = epochs[i - 1].label
      }
    }
  }

  // 5. Agrupar épocas contiguas en Bloques y registrar Transiciones
  const blocks = []
  const transitions = []
  let curBlock = null

  epochs.forEach((ep, idx) => {
    if (!curBlock || curBlock.stage !== ep.stage) {
      if (curBlock) {
        curBlock.durationMin = Math.max(1, Math.round((ep.time - curBlock.start) / 60000))
        blocks.push(curBlock)

        // Registrar momento exacto de transición
        transitions.push({
          time: fmtClock(ep.time),
          timestamp: ep.time,
          fromStage: curBlock.stage,
          fromLabel: curBlock.label,
          toStage: ep.stage,
          toLabel: ep.label,
          toColor: ep.color,
          durationBeforeMin: curBlock.durationMin,
        })
      }
      curBlock = {
        start: ep.time,
        startTimeLabel: ep.timeLabel,
        stage: ep.stage,
        level: ep.level,
        color: ep.color,
        label: ep.label,
        desc: ep.desc,
        hrs: [ep.hr],
        rmssds: [ep.rmssd],
      }
    } else {
      curBlock.hrs.push(ep.hr)
      curBlock.rmssds.push(ep.rmssd)
    }

    if (idx === epochs.length - 1 && curBlock) {
      curBlock.durationMin = Math.max(1, Math.round((ep.time + epochSizeMs - curBlock.start) / 60000))
      blocks.push(curBlock)
    }
  })

  // 6. Estadísticas Globales de la Noche
  let deepMin = 0, remMin = 0, lightMin = 0, awakeMin = 0, awakeCount = 0

  blocks.forEach(b => {
    if (b.stage === 'deep')  deepMin += b.durationMin
    if (b.stage === 'rem')   remMin += b.durationMin
    if (b.stage === 'light') lightMin += b.durationMin
    if (b.stage === 'awake') {
      awakeMin += b.durationMin
      awakeCount++
    }
  })

  const totalSleepMin = deepMin + remMin + lightMin
  const timeInBedMin  = totalSleepMin + awakeMin
  const efficiencyPct = timeInBedMin > 0 ? Math.round((totalSleepMin / timeInBedMin) * 100) : 0

  const deepPct  = totalSleepMin > 0 ? Math.round((deepMin / totalSleepMin) * 100) : 0
  const remPct   = totalSleepMin > 0 ? Math.round((remMin / totalSleepMin) * 100) : 0
  const lightPct = totalSleepMin > 0 ? Math.round((lightMin / totalSleepMin) * 100) : 0
  const awakePct = timeInBedMin > 0 ? Math.round((awakeMin / timeInBedMin) * 100) : 0

  // Diagnóstico de calidad de fases según estándares clínicos AASM
  const deepStatus  = deepPct >= 18 ? 'Excelente' : deepPct >= 12 ? 'Normal' : 'Bajo'
  const remStatus   = remPct >= 20 ? 'Excelente' : remPct >= 15 ? 'Normal' : 'Bajo'
  const awakeStatus = awakeCount <= 3 ? 'Bajo (Sueño continuo)' : awakeCount <= 6 ? 'Moderado' : 'Frecuente'

  // 7. Cálculo del Sleep Score Global Holístico (0 a 100)
  const effScore  = Math.min(35, Math.round((efficiencyPct / 85) * 35))
  const deepScore = Math.min(25, Math.round((deepPct / 18) * 25))
  const remScore  = Math.min(25, Math.round((remPct / 20) * 25))
  const contScore = Math.max(0, 15 - Math.max(0, awakeCount - 2) * 2.5)
  const sleepScore = Math.min(100, Math.max(20, Math.round(effScore + deepScore + remScore + contScore)))

  let sleepScoreLabel = 'Óptimo'
  let sleepScoreColor = '#30d158'
  if (sleepScore < 65) {
    sleepScoreLabel = 'Fragmentado'
    sleepScoreColor = '#ff3b30'
  } else if (sleepScore < 80) {
    sleepScoreLabel = 'Aceptable'
    sleepScoreColor = '#ff9f0a'
  } else if (sleepScore >= 90) {
    sleepScoreLabel = 'Excelente'
    sleepScoreColor = '#30d158'
  }

  // 8. Ciclos Ultradianos Fisiológicos (~90 minutos cada ciclo)
  const cycleMs = 90 * 60 * 1000
  const totalNightMs = endMs - startMs
  const numCycles = Math.max(1, Math.round(totalNightMs / cycleMs))
  const cycles = []
  for (let c = 0; c < numCycles; c++) {
    const cStart = startMs + c * cycleMs
    const cEnd = Math.min(endMs, cStart + cycleMs)
    cycles.push({
      num: c + 1,
      start: cStart,
      end: cEnd,
      startLabel: fmtClock(cStart),
      endLabel: fmtClock(cEnd),
      durationMin: Math.round((cEnd - cStart) / 60000),
    })
  }

  return {
    epochs,
    blocks,
    transitions,
    totalSleepMin,
    totalSleepFormatted: formatDuration(totalSleepMin),
    timeInBedMin,
    timeInBedFormatted: formatDuration(timeInBedMin),
    efficiencyPct,
    sleepScore,
    sleepScoreLabel,
    sleepScoreColor,
    cycles,
    nadirHr,
    avgSleepHr,
    bedTime: fmtClock(startMs),
    wakeTime: fmtClock(endMs),
    stages: {
      deep:  { mins: deepMin,  pct: deepPct,  status: deepStatus,  formatted: formatDuration(deepMin),  cfg: SLEEP_STAGES_CONFIG.deep },
      rem:   { mins: remMin,   pct: remPct,   status: remStatus,   formatted: formatDuration(remMin),   cfg: SLEEP_STAGES_CONFIG.rem },
      light: { mins: lightMin, pct: lightPct, status: 'Normal',    formatted: formatDuration(lightMin), cfg: SLEEP_STAGES_CONFIG.light },
      awake: { mins: awakeMin, pct: awakePct, status: awakeStatus, formatted: formatDuration(awakeMin), cfg: SLEEP_STAGES_CONFIG.awake, count: awakeCount },
    }
  }
}
